

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fileService } from '../services/fileService';
import { formatDate } from '../utils/dateUtils';

const FileViewer = () => {
  const navigate = useNavigate();
  const [fileTypes, setFileTypes] = useState([]);
  const [selectedFileType, setSelectedFileType] = useState('');
  const [selectedFile, setSelectedFile] = useState('');
  const [fileOptions, setFileOptions] = useState({});
  const [fileData, setFileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');  // File type display names
  const [embeddedUrl, setEmbeddedUrl] = useState(null);
  const [embeddedObjectUrl, setEmbeddedObjectUrl] = useState(null);
  const fileTypeDisplayNames = {
    modelSecurity: 'Model Security',
    allocationModelMembers: 'Allocation Model Members',
    modelSecurityInformation: 'Model Security Information',
    accountHoldings: 'Account Holdings',
    accountAlternateSecuritySets: 'Account Alternate Security Sets',
    accountSubstituteSecurities: 'Account Substitute Securities',
    accountSecurityRestrictions: 'Account Security Restrictions'
  };

  // Fetch the list of available files when component mounts
  useEffect(() => {
    fetchFileList();
    // Only call once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the list of files from the backend
  const fetchFileList = useCallback(async () => {
    setLoading(true);
    setError('');
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to view files');
      setLoading(false);
      return;
    }
    try {
      const data = await fileService.listFiles();
      // Organize files by type, extracting type from stored_filename
      const filesByType = {};
      if (data.files && data.files.length > 0) {
        data.files.forEach(file => {
          // Derive file_type from stored_filename prefix (before first '_')
          let type = file.stored_filename.split('_')[0];
          if (!filesByType[type]) {
            filesByType[type] = [];
          }
          filesByType[type].push({
            stored_filename: file.stored_filename,
            original_filename: file.original_filename,
            upload_date: file.upload_date,
            file_size: file.size
          });
        });
      }
      const availableFileTypes = Object.keys(filesByType);
      setFileTypes(availableFileTypes);
      setFileOptions(filesByType);
      if (availableFileTypes.length > 0) {
        setSelectedFileType(availableFileTypes[0]);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
        // Redirect to login will be handled by the response interceptor
      } else {
        setError(err.message || 'An error occurred while fetching files');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the selected file when file type or file selection changes
  useEffect(() => {
    if (selectedFileType && fileOptions[selectedFileType]?.length > 0) {
      // Set the first file as selected by default
      setSelectedFile(fileOptions[selectedFileType][0].stored_filename);
    } else {
      setSelectedFile('');
    }
  }, [selectedFileType, fileOptions]);

  // Handle file type selection change
  const handleFileTypeChange = (e) => {
    setSelectedFileType(e.target.value);
    setFileData(null); // Clear previous file data
  };

  // Handle file selection change
  const handleFileChange = (e) => {
    setSelectedFile(e.target.value);
    setFileData(null); // Clear previous file data
  };

  // View the selected file
  const handleViewFile = async () => {
    if (!selectedFileType || !selectedFile) {
      setError('Please select a file to view');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Get file content using the new service
      const response = await fileService.downloadFile(selectedFile);

      // If downloadFile returned a presigned URL object, embed in viewer when possible
      if (response && response.redirected && response.url) {
        const downloadUrl = response.url;
        // If the file is a PDF, render it in the embedded viewer
        if (selectedFile.toLowerCase().endsWith('.pdf')) {
          setFileData(null);
          setEmbeddedUrl(downloadUrl);
          setLoading(false);
          return;
        }
        // For other binary types fall back to opening in new tab
        window.open(downloadUrl, '_blank');
        setLoading(false);
        return;
      }

      // Convert blob to text (if blob) or use string
      let text;
      if (response && response.data instanceof Blob) {
        // If it's a PDF or other binary type, open in new tab instead of trying to parse
        const mimeType = response.data.type || '';
        if (mimeType === 'application/pdf') {
          // Create an object URL and embed it in the viewer
          const url = URL.createObjectURL(response.data);
          setFileData(null);
          setEmbeddedObjectUrl(url);
          setEmbeddedUrl(url);
          return;
        }
        text = await response.data.text();
      } else {
        text = String(response && response.data ? response.data : response);
      }
      
      // Parse the content and format it to match the old UI expectations
      let parsedData;
      if (selectedFile.toLowerCase().endsWith('.csv')) {
        // Parse CSV content
        const lines = text.split('\n');
        const headers = lines[0] ? lines[0].split(',').map(h => h.trim().replace(/"/g, '')) : [];
        const rows = lines.slice(1).filter(line => line.trim()).map(line => 
          line.split(',').map(cell => cell.trim().replace(/"/g, ''))
        );
        
        parsedData = {
          file_type: selectedFileType,
          filename: selectedFile,
          headers: headers,
          rows: rows
        };
      } else {
        // For other file types, try to parse as JSON or display as text
        try {
          const jsonData = JSON.parse(text);
          parsedData = {
            file_type: selectedFileType,
            filename: selectedFile,
            headers: ['Content'],
            rows: [[JSON.stringify(jsonData, null, 2)]]
          };
        } catch {
          parsedData = {
            file_type: selectedFileType,
            filename: selectedFile,
            headers: ['Content'],
            rows: [[text]]
          };
        }
      }
      
      setFileData(parsedData);
    } catch (err) {
      setError(err.message || 'An error occurred while fetching file content');
      console.error('Error fetching file content:', err);
    } finally {
      setLoading(false);
    }
  };

  // Close embedded viewer and revoke any created object URL
  const closeEmbeddedViewer = () => {
    if (embeddedObjectUrl) {
      try {
        URL.revokeObjectURL(embeddedObjectUrl);
      } catch (e) {
        // ignore
      }
      setEmbeddedObjectUrl(null);
    }
    setEmbeddedUrl(null);
  };

  // Render file selection dropdown options
  const renderFileOptions = () => {
    if (!selectedFileType || !fileOptions[selectedFileType]) {
      return <option value="">No files available</option>;
    }
    
    return fileOptions[selectedFileType].map((file, index) => (
      <option key={index} value={file.stored_filename}>
        {file.original_filename} (Uploaded: {formatDate(file.upload_date)})
      </option>
    ));
  };

  return (
    <div className="container">
      <h2 className="mb-4">File Viewer</h2>
      
      {error && (
        <div className="alert alert-danger mb-4">
          {error}
        </div>
      )}
      
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-5">
              <label htmlFor="fileType" className="form-label">File Type</label>
              <select 
                id="fileType" 
                className="form-select"
                value={selectedFileType}
                onChange={handleFileTypeChange}
                disabled={loading || fileTypes.length === 0}
              >
                {fileTypes.length === 0 ? (
                  <option value="">No file types available</option>
                ) : (
                  fileTypes.map(type => (
                    <option key={type} value={type}>
                      {fileTypeDisplayNames[type] || type}
                    </option>
                  ))
                )}
              </select>
            </div>
            
            <div className="col-md-5">
              <label htmlFor="fileName" className="form-label">File Name</label>
              <select 
                id="fileName" 
                className="form-select"
                value={selectedFile}
                onChange={handleFileChange}
                disabled={loading || !selectedFileType || !fileOptions[selectedFileType]?.length}
              >
                {renderFileOptions()}
              </select>
            </div>
            
            <div className="col-md-2 d-flex align-items-end">
              <button 
                className="btn btn-primary w-100"
                onClick={handleViewFile}
                disabled={loading || !selectedFile}
              >
                {loading ? 'Loading...' : 'View File'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {fileData && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{fileTypeDisplayNames[fileData.file_type] || fileData.file_type} Data</h5>
            <span className="badge bg-secondary">{fileData.filename}</span>
          </div>
          <div className="card-body">
            {fileData.headers && fileData.rows && (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-light">
                    <tr>
                      {fileData.headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fileData.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {(!fileData.headers || !fileData.rows || fileData.rows.length === 0) && (
              <div className="alert alert-info">
                No data available in this file.
              </div>
            )}
          </div>
        </div>
      )}

      {embeddedUrl && (
        <div className="card mt-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Embedded File Viewer</h5>
            <div>
              <button className="btn btn-sm btn-outline-secondary me-2" onClick={closeEmbeddedViewer}>Close</button>
            </div>
          </div>
          <div className="card-body">
            {/* Use iframe for embedding presigned URL (navigation not XHR) */}
            <div style={{ width: '100%', height: '800px' }}>
              <iframe
                title="EmbeddedFile"
                src={embeddedUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}
      
      {!fileData && !loading && (
        <div className="alert alert-info">
          Select a file type and file to view its contents.
        </div>
      )}
      
      <div className="mt-4">
        <button 
          className="btn btn-outline-secondary me-2" 
          onClick={() => navigate('/')}
        >
          Back to Dashboard
        </button>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/import')}
        >
          Import New Files
        </button>
      </div>
    </div>
  );
};

export default FileViewer;
